import { Helmet } from 'react-helmet';

import { VyPageLayout } from '../../components/new-design/layout/VyPageLayout';
import { useSearchParams } from 'react-router-dom';
import { CreateRefundBottomToolbar } from './components/CreateRefundBottomToolbar';
import { RefundFormSchema, RefundFormValues } from './refund-form-schema';
import { extract, fromNullToUndefined, vyDate } from '@vy/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCatalogProductsList } from '../../hooks/requests/use-catalog-products-list';
import { useCreateInvoice } from '../invoices/CreateInvoicePage/create-invoice-page-components/CreateInvoiceForm/CreateInvoiceForm';
import { useVyNavigate } from '../../hooks/use-vy-navigate';
import { getDefaultRefundProduct } from '../plans/CreatePlanPage/create-plan-page-consts/add-item-button-consts';
import { CreateInvoiceRequestPayload } from '@vy/api-definition';
import { DEFAULT_CURRENCY, PAYMENT_TERMS, BILLING_INTERVALS, PRICING_MODELS } from '@vy/types';
import { runMutation } from '../../utils/vy-mutation';
import { CreateRefundFormContent } from './components/CreateRefundFormContent';
import { useForm } from 'react-hook-form';

export const CreateRefundPage: React.FC = () => {
  const [queryParams] = useSearchParams();
  const createInvoiceMutation = useCreateInvoice();
  const navigate = useVyNavigate();
  const customerIdFromParams = queryParams.get('customerId');

  const { control, handleSubmit, reset } = useForm<RefundFormValues>({
    defaultValues: {
      customerId: fromNullToUndefined(customerIdFromParams),
      date: vyDate(new Date()).toDate(),
    },
    resolver: zodResolver(RefundFormSchema),
  });

  const { data: catalogProductsData } = useCatalogProductsList();
  const catalogProducts = catalogProductsData?.catalogProducts ?? [];
  const catalogProductsNames = extract(catalogProducts, 'name');

  const submitForm = async (data: RefundFormValues) => {
    const requestPayload = buildRefundPayload(data);

    await runMutation(
      createInvoiceMutation,
      requestPayload,
      {
        pending: 'Issuing refund...',
        success: 'Refund issued',
        error: 'Failed to issue refund',
      },
      {
        onSuccess: async ({ invoiceId, customerId }) => {
          navigate(`/invoices/${invoiceId}/customer/${customerId}/dynamic`);
        },
      },
    );
  };

  return (
    <VyPageLayout
      path={['Refund', 'Create']}
      title="Create Refund"
      sx={{
        padding: '0',
      }}
    >
      <Helmet>
        <title>Create Credit Note</title>
      </Helmet>

      <form
        onSubmit={handleSubmit(submitForm)}
        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
      >
        <CreateRefundFormContent control={control} catalogProductsNames={catalogProductsNames} />
        <CreateRefundBottomToolbar handleClearForm={reset} />
      </form>
    </VyPageLayout>
  );
};

function buildRefundPayload(data: RefundFormValues) {
  const refundDefaultValues = getDefaultRefundProduct();

  const payload: CreateInvoiceRequestPayload = {
    fixedProducts: [
      {
        ...refundDefaultValues,
        variants: [
          {
            name: data.serviceName,
            pricing: {
              pricingModel: PRICING_MODELS.Refund,
              priceConfiguration: {
                price: data.amount * -1,
                shouldCreateSeparateInvoice: false,
              },
            },
          },
        ],
        paymentTerm: PAYMENT_TERMS.Prepayment,
        billingInterval: BILLING_INTERVALS.Monthly,
        isPeriodicVariants: false,
      },
    ],
    settings: {
      customerId: data.customerId,
      startDate: data.date.toISOString(),
      endDate: data.date.toISOString(),
      currency: DEFAULT_CURRENCY,
      paymentDueDays: 0,
    },
  };
  return payload;
}
